// ============================================
// REQUERIMIENTOS.JS — Captura rápida y gestión
// de requerimientos operativos
// ============================================

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { BarraBusqueda, BarraPestanas, filtrarPorTexto } from './utils/filtros';
import ModoLevantamiento from './components/ModoLevantamiento';

// ============================================
// CONSTANTES
// ============================================
const PP = "'Poppins', sans-serif";
const colores = {
  principal: '#5c9adb',
  oscuro: '#2f5f9e',
  verde: '#5cd4b0',
  rojo: '#e05c5c',
  amarillo: '#dcde74',
  fondo: '#f3f4f4',
  gris: '#4e5357',
  grisClaro: '#5e666e',
};

const TIPOS = [
  { id: 'correctivo', label: 'Servicio correctivo', icono: '🔧', desc: 'Algo que no funciona', color: '#e05c5c', bg: '#fff0f0' },
  { id: 'mantenimiento', label: 'Mantenimiento', icono: '🔩', desc: 'Servicio preventivo programado', color: '#f4a261', bg: '#fff8f0' },
  { id: 'proyecto', label: 'Proyecto nuevo', icono: '📐', desc: 'Instalación nueva', color: '#5c9adb', bg: '#eef5ff' },
  { id: 'venta', label: 'Venta', icono: '💼', desc: 'Cliente interesado en comprar', color: '#5cd4b0', bg: '#f0faf6' },
  { id: 'adicional', label: 'Adicional en proyecto', icono: '🔄', desc: 'Surgió algo extra en un trabajo activo', color: '#a78bfa', bg: '#f5f0ff' },
  { id: 'otro', label: 'Otro', icono: '📋', desc: 'Cualquier otra cosa', color: '#8fb8e8', bg: '#f0f5fa' },
];

const URGENCIAS = [
  { id: 'normal', label: 'Normal', color: '#5cd4b0', bg: '#f0faf6' },
  { id: 'urgente', label: 'Urgente', color: '#f4a261', bg: '#fff8f0' },
  { id: 'critico', label: 'Critico', color: '#e05c5c', bg: '#fff0f0' },
];

const MEDIOS = ['WhatsApp', 'Llamada', 'Correo', 'En persona', 'Zoom', 'Otro'];

const CLIENTES_EXISTENTES = [
  'Bodegas Alianza', 'Farma Plus', 'Hospital Zona Norte', 'Plaza Comercial Sur',
  'Grupo Regio', 'C. Com. Nexus', 'Of. Corporativo Lux', 'Hotel Sierra Madre',
];

const SUCURSALES_POR_CLIENTE = {
  'Bodegas Alianza': [
    { nombre: 'Sucursal Norte', direccion: 'Av. Insurgentes Norte 1234', ciudad: 'CDMX' },
    { nombre: 'Sucursal Sur', direccion: 'Calz. de Tlalpan 567', ciudad: 'CDMX' },
    { nombre: 'Sucursal Oriente', direccion: 'Blvd. Puerto Aéreo 890', ciudad: 'CDMX' },
    { nombre: 'Sucursal Poniente', direccion: 'Av. Observatorio 321', ciudad: 'CDMX' },
    { nombre: 'Sucursal Cuernavaca 1', direccion: 'Av. Morelos 100', ciudad: 'Cuernavaca' },
    { nombre: 'Sucursal Cuernavaca 2', direccion: 'Blvd. Juárez 450', ciudad: 'Cuernavaca' },
    { nombre: 'Sucursal Cuernavaca 3', direccion: 'Av. Plan de Ayala 78', ciudad: 'Cuernavaca' },
    { nombre: 'Sucursal Querétaro', direccion: 'Blvd. Bernardo Quintana 200', ciudad: 'Querétaro' },
    { nombre: 'Sucursal Satélite', direccion: 'Circuito Médicos 45', ciudad: 'EDOMEX' },
    { nombre: 'Sucursal Tlalnepantla', direccion: 'Av. Mario Colín 890', ciudad: 'EDOMEX' },
  ],
  'Farma Plus': [
    { nombre: 'Matriz', direccion: 'Av. Revolución 100', ciudad: 'CDMX' },
    { nombre: 'Sucursal Polanco', direccion: 'Masaryk 45', ciudad: 'CDMX' },
    { nombre: 'Sucursal Satélite', direccion: 'Circuito Médicos 12', ciudad: 'EDOMEX' },
  ],
};
const SUCURSAL_DEFAULT = [{ nombre: 'Matriz / Oficina principal', direccion: '', ciudad: '' }];
const SUCURSAL_NO_SE = { nombre: 'No sé / General', direccion: '', ciudad: '' };

const COLORES_CIUDAD = {
  'CDMX': { bg: '#eef5ff', color: '#2f5f9e' },
  'Cuernavaca': { bg: '#f0faf6', color: '#1D9E75' },
  'Querétaro': { bg: '#fff8f0', color: '#c45e00' },
  'EDOMEX': { bg: '#f5f0ff', color: '#7c3aed' },
};

function normalizar(texto) {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

const PROYECTOS_ACTIVOS = [
  { id: 1, nombre: 'PRY-2026-014 — Farma Plus HVAC' },
  { id: 2, nombre: 'PRY-2026-012 — C. Com. Nexus Eléctrico' },
  { id: 3, nombre: 'PRY-2026-010 — Hospital Zona Norte Plomería' },
];

// Datos de ejemplo
const requerimientosInit = [
  {
    id: 1, folio: 'REQ-2026-001', cliente: 'Bodegas Alianza', clienteNuevo: false,
    tipo: 'correctivo', urgencia: 'urgente', medio: 'WhatsApp',
    descripcion: 'El aire acondicionado del almacén 3 no enciende desde ayer por la mañana. Están almacenando producto perecedero.',
    recibio: 'Laura S.', fecha: '2026-04-04T09:15:00',
    estado: 'nuevo',
    notas: [],
    timeline: [
      { fecha: '2026-04-04T09:15:00', texto: 'Requerimiento creado', autor: 'Laura S.' },
    ],
    historialCliente: [
      { tipo: 'servicio', descripcion: 'Mantenimiento HVAC trimestral', fecha: '2026-02-15' },
      { tipo: 'equipo', descripcion: '3 unidades HVAC instaladas', fecha: '2025-11-20' },
    ],
  },
  {
    id: 2, folio: 'REQ-2026-002', cliente: 'Farma Plus', clienteNuevo: false,
    tipo: 'mantenimiento', urgencia: 'normal', medio: 'Correo',
    descripcion: 'Solicitan programar mantenimiento preventivo de los equipos de refrigeración de la sucursal Centro.',
    recibio: 'Carlos V.', fecha: '2026-04-03T14:30:00',
    estado: 'enproceso', asignado: 'Miguel G.',
    notas: [{ texto: 'Se contactó al cliente para confirmar fecha', autor: 'Miguel G.', fecha: '2026-04-04T10:00:00' }],
    timeline: [
      { fecha: '2026-04-03T14:30:00', texto: 'Requerimiento creado', autor: 'Carlos V.' },
      { fecha: '2026-04-04T10:00:00', texto: 'Asignado a Miguel G.', autor: 'Laura S.' },
    ],
    historialCliente: [
      { tipo: 'proyecto', descripcion: 'PRY-2026-014 — Instalación HVAC', fecha: '2026-01-10' },
      { tipo: 'servicio', descripcion: 'Correctivo bomba agua', fecha: '2025-09-05' },
    ],
  },
  {
    id: 3, folio: 'REQ-2026-003', cliente: 'Cliente nuevo: Roberto Méndez', clienteNuevo: true,
    tipo: 'venta', urgencia: 'normal', medio: 'Llamada',
    descripcion: 'Interesado en sistema de CCTV para nave industrial de 2000m2. Pide cotización.',
    recibio: 'Carlos V.', fecha: '2026-04-03T11:00:00',
    estado: 'enproceso', asignado: 'Carlos V.',
    notas: [],
    timeline: [
      { fecha: '2026-04-03T11:00:00', texto: 'Requerimiento creado', autor: 'Carlos V.' },
      { fecha: '2026-04-03T11:30:00', texto: 'Se envió correo de contacto inicial', autor: 'Carlos V.' },
    ],
    historialCliente: [],
  },
  {
    id: 4, folio: 'REQ-2026-004', cliente: 'Grupo Regio', clienteNuevo: false,
    tipo: 'proyecto', urgencia: 'normal', medio: 'En persona',
    descripcion: 'Quieren instalar sistema de control de acceso en sus 3 sucursales. Necesitan levantamiento.',
    recibio: 'Laura S.', fecha: '2026-04-02T16:45:00',
    estado: 'enproceso', asignado: 'Pedro R.',
    notas: [{ texto: 'Levantamiento programado para el lunes 7 de abril', autor: 'Pedro R.', fecha: '2026-04-03T09:00:00' }],
    timeline: [
      { fecha: '2026-04-02T16:45:00', texto: 'Requerimiento creado', autor: 'Laura S.' },
      { fecha: '2026-04-03T09:00:00', texto: 'Asignado a Pedro R.', autor: 'Laura S.' },
      { fecha: '2026-04-03T09:00:00', texto: 'Levantamiento programado', autor: 'Pedro R.' },
    ],
    historialCliente: [
      { tipo: 'contrato', descripcion: 'Contrato mantenimiento anual', fecha: '2025-06-01' },
    ],
  },
  {
    id: 5, folio: 'REQ-2026-005', cliente: 'Farma Plus', clienteNuevo: false,
    tipo: 'adicional', urgencia: 'urgente', medio: 'WhatsApp', proyectoRelacionado: 'PRY-2026-014 — Farma Plus HVAC',
    descripcion: 'Durante la instalación del HVAC encontraron que el ducto principal tiene fuga. Necesitan reparación antes de continuar.',
    recibio: 'Miguel G.', fecha: '2026-04-04T11:20:00',
    estado: 'nuevo',
    notas: [],
    timeline: [
      { fecha: '2026-04-04T11:20:00', texto: 'Requerimiento creado desde campo', autor: 'Miguel G.' },
    ],
    historialCliente: [
      { tipo: 'proyecto', descripcion: 'PRY-2026-014 — Instalación HVAC', fecha: '2026-01-10' },
    ],
  },
  {
    id: 6, folio: 'REQ-2026-006', cliente: 'Hospital Zona Norte', clienteNuevo: false,
    tipo: 'correctivo', urgencia: 'critico', medio: 'Llamada',
    descripcion: 'Falla total en el sistema eléctrico del ala de urgencias. Sin energía de respaldo.',
    recibio: 'Laura S.', fecha: '2026-04-01T07:30:00',
    estado: 'historial', resolucion: 'Se envió equipo de emergencia. Falla en transferencia automática reparada en sitio.',
    notas: [
      { texto: 'Equipo en camino', autor: 'Laura S.', fecha: '2026-04-01T07:45:00' },
      { texto: 'Llegamos al sitio, diagnosticando', autor: 'Miguel G.', fecha: '2026-04-01T08:30:00' },
      { texto: 'Reparación completada', autor: 'Miguel G.', fecha: '2026-04-01T11:00:00' },
    ],
    timeline: [
      { fecha: '2026-04-01T07:30:00', texto: 'Requerimiento creado — CRITICO', autor: 'Laura S.' },
      { fecha: '2026-04-01T07:45:00', texto: 'Asignado a Miguel G. + Pedro R.', autor: 'Laura S.' },
      { fecha: '2026-04-01T08:30:00', texto: 'Equipo en sitio', autor: 'Miguel G.' },
      { fecha: '2026-04-01T11:00:00', texto: 'Reparación completada', autor: 'Miguel G.' },
      { fecha: '2026-04-01T11:30:00', texto: 'Requerimiento cerrado', autor: 'Laura S.' },
    ],
    historialCliente: [
      { tipo: 'proyecto', descripcion: 'PRY-2026-010 — Plomería', fecha: '2025-12-01' },
      { tipo: 'servicio', descripcion: 'Mantenimiento eléctrico semestral', fecha: '2025-10-15' },
    ],
  },
];

// ============================================
// HELPERS
// ============================================
function tiempoTranscurrido(fechaStr) {
  const ahora = new Date();
  const fecha = new Date(fechaStr);
  const diff = ahora - fecha;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const dias = Math.floor(hrs / 24);
  return `${dias}d`;
}

function formatFecha(fechaStr) {
  const d = new Date(fechaStr);
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function generarFolioReq(existentes) {
  const anio = new Date().getFullYear();
  const nums = existentes.map(r => {
    const parts = r.folio.split('-');
    return parseInt(parts[2]) || 0;
  });
  const siguiente = nums.length > 0 ? Math.max(...nums) + 1 : 1;
  return `REQ-${anio}-${String(siguiente).padStart(3, '0')}`;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function Requerimientos({ pestanaInicial = 'nuevo', abrirFormulario = false, usuario = 'Laura S.', esCelular = false }) {
  const [requerimientos, setRequerimientos] = useState(requerimientosInit);
  const [pestana, setPestana] = useState(pestanaInicial);
  const [busqueda, setBusqueda] = useState('');
  const [seleccionado, setSeleccionado] = useState(null);
  const [formularioAbierto, setFormularioAbierto] = useState(abrirFormulario);
  const [nuevaNota, setNuevaNota] = useState('');
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);
  const [motivoCierre, setMotivoCierre] = useState('');
  const [modoLevantamiento, setModoLevantamiento] = useState(false);
  const [modalAgrupar, setModalAgrupar] = useState(null); // { nuevo, existentes }

  // Formulario multi-paso
  const [paso, setPaso] = useState(1);
  const [formData, setFormData] = useState({
    cliente: '', clienteNuevo: false, sucursal: '', medio: '', descripcion: '',
    tipo: '', urgencia: 'normal', proyectoRelacionado: '',
  });
  const [buscandoCliente, setBuscandoCliente] = useState('');
  const [busquedaSucursal, setBusquedaSucursal] = useState('');
  const [filtroCiudad, setFiltroCiudad] = useState('');
  const [grabando, setGrabando] = useState(false);
  const recognitionRef = useRef(null);

  const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const esMovil = esCelular || width < 768;

  // Abrir formulario si la prop lo indica
  useEffect(() => {
    if (abrirFormulario) setFormularioAbierto(true);
  }, [abrirFormulario]);

  // Filtrar por pestaña y búsqueda
  const filtrados = useMemo(() => {
    let lista = requerimientos.filter(r => r.estado === pestana);
    if (busqueda) {
      lista = filtrarPorTexto(lista, busqueda, ['folio', 'cliente', 'descripcion', 'recibio', 'tipo']);
    }
    return lista.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [requerimientos, pestana, busqueda]);

  const conteos = useMemo(() => ({
    nuevo: requerimientos.filter(r => r.estado === 'nuevo').length,
    enproceso: requerimientos.filter(r => r.estado === 'enproceso').length,
    historial: requerimientos.filter(r => r.estado === 'historial').length,
  }), [requerimientos]);

  // Clientes filtrados para búsqueda
  const clientesFiltrados = buscandoCliente.length >= 1
    ? CLIENTES_EXISTENTES.filter(c => c.toLowerCase().includes(buscandoCliente.toLowerCase()))
    : [];

  // ============================================
  // DICTADO POR VOZ
  // ============================================
  const toggleVoz = () => {
    if (grabando) {
      if (recognitionRef.current) recognitionRef.current.stop();
      setGrabando(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Tu navegador no soporta dictado por voz'); return; }
    const recognition = new SR();
    recognition.lang = 'es-MX';
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.onresult = (e) => {
      let transcript = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) transcript += e.results[i][0].transcript;
      }
      if (transcript) {
        setFormData(prev => ({ ...prev, descripcion: prev.descripcion + (prev.descripcion ? ' ' : '') + transcript }));
      }
    };
    recognition.onerror = () => setGrabando(false);
    recognition.onend = () => setGrabando(false);
    recognitionRef.current = recognition;
    recognition.start();
    setGrabando(true);
  };

  // ============================================
  // ACCIONES
  // ============================================
  const resetFormulario = () => {
    setPaso(1);
    setFormData({ cliente: '', clienteNuevo: false, sucursal: '', medio: '', descripcion: '', tipo: '', urgencia: 'normal', proyectoRelacionado: '' });
    setBuscandoCliente('');
    setBusquedaSucursal('');
    setFiltroCiudad('');
    setFormularioAbierto(false);
    if (grabando && recognitionRef.current) recognitionRef.current.stop();
    setGrabando(false);
  };

  const crearRequerimiento = () => {
    const nuevo = {
      id: Date.now(),
      folio: generarFolioReq(requerimientos),
      cliente: formData.clienteNuevo ? `Cliente nuevo: ${formData.cliente}` : formData.cliente,
      clienteNuevo: formData.clienteNuevo,
      sucursal: formData.clienteNuevo ? '' : (formData.sucursal ? (formData.sucursal.nombre + (formData.sucursal.direccion ? ' — ' + formData.sucursal.direccion : '')) : ''),
      tipo: formData.tipo,
      urgencia: formData.urgencia,
      medio: formData.medio,
      descripcion: formData.descripcion,
      recibio: usuario,
      fecha: new Date().toISOString(),
      estado: 'nuevo',
      notas: [],
      proyectoRelacionado: formData.proyectoRelacionado || undefined,
      timeline: [{ fecha: new Date().toISOString(), texto: 'Requerimiento creado', autor: usuario }],
      historialCliente: [],
    };
    // Revisar si ya hay requerimientos pendientes del mismo cliente + sucursal
    const mismoClienteSucursal = requerimientos.filter(r =>
      r.estado !== 'historial' &&
      r.cliente === nuevo.cliente &&
      (r.sucursal || '') === (nuevo.sucursal || '')
    );
    if (mismoClienteSucursal.length > 0 && !nuevo.clienteNuevo) {
      setModalAgrupar({ nuevo, existentes: mismoClienteSucursal });
      return;
    }
    setRequerimientos(prev => [nuevo, ...prev]);
    resetFormulario();
    setPestana('nuevo');
  };

  const confirmarAgrupar = (agrupar) => {
    if (!modalAgrupar) return;
    const { nuevo, existentes } = modalAgrupar;
    const marcado = agrupar
      ? { ...nuevo, grupoVisitaId: existentes[0].grupoVisitaId || existentes[0].id, agrupado: true }
      : nuevo;
    setRequerimientos(prev => {
      let lista = [marcado, ...prev];
      if (agrupar) {
        const gid = marcado.grupoVisitaId;
        lista = lista.map(r =>
          existentes.find(e => e.id === r.id)
            ? { ...r, grupoVisitaId: gid, agrupado: true }
            : r
        );
      }
      return lista;
    });
    setModalAgrupar(null);
    resetFormulario();
    setPestana('nuevo');
  };

  // Escuchar requerimientos creados desde el Asistente Beep (Modo Levantamiento IA)
  useEffect(() => {
    const handler = (e) => {
      if (Array.isArray(e.detail)) crearDesdeIA(e.detail);
    };
    window.addEventListener('beep:crearDesdeIA', handler);
    return () => window.removeEventListener('beep:crearDesdeIA', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requerimientos]);

  const crearDesdeIA = (listaIA) => {
    const ahora = new Date().toISOString();
    const nuevos = listaIA.map((r, i) => ({
      id: Date.now() + i,
      folio: generarFolioReq([...requerimientos, ...listaIA.slice(0, i).map((_, j) => ({ folio: `REQ-${new Date().getFullYear()}-${String(requerimientos.length + j + 1).padStart(3, '0')}` }))]),
      cliente: 'Levantamiento IA',
      clienteNuevo: false,
      tipo: r.tipo || 'otro',
      urgencia: r.urgencia || 'normal',
      medio: 'En persona',
      descripcion: r.descripcion + (r.equipos ? ` (Equipos: ${r.equipos})` : ''),
      recibio: usuario,
      fecha: ahora,
      estado: 'nuevo',
      notas: [],
      timeline: [{ fecha: ahora, texto: 'Creado desde Modo Levantamiento (IA)', autor: usuario }],
      historialCliente: [],
    }));
    setRequerimientos(prev => [...nuevos, ...prev]);
    setModoLevantamiento(false);
    setPestana('nuevo');
  };

  const agregarNota = () => {
    if (!nuevaNota.trim() || !seleccionado) return;
    setRequerimientos(prev => prev.map(r => {
      if (r.id !== seleccionado.id) return r;
      return {
        ...r,
        notas: [...r.notas, { texto: nuevaNota, autor: usuario, fecha: new Date().toISOString() }],
        timeline: [...r.timeline, { fecha: new Date().toISOString(), texto: nuevaNota, autor: usuario }],
      };
    }));
    setSeleccionado(prev => ({
      ...prev,
      notas: [...prev.notas, { texto: nuevaNota, autor: usuario, fecha: new Date().toISOString() }],
      timeline: [...prev.timeline, { fecha: new Date().toISOString(), texto: nuevaNota, autor: usuario }],
    }));
    setNuevaNota('');
  };

  const cerrarRequerimiento = () => {
    if (!motivoCierre.trim()) return;
    setRequerimientos(prev => prev.map(r => {
      if (r.id !== seleccionado.id) return r;
      return {
        ...r, estado: 'historial', resolucion: motivoCierre,
        timeline: [...r.timeline, { fecha: new Date().toISOString(), texto: `Cerrado: ${motivoCierre}`, autor: usuario }],
      };
    }));
    setSeleccionado(null);
    setConfirmandoCierre(false);
    setMotivoCierre('');
  };

  // ============================================
  // ESTILOS COMUNES
  // ============================================
  const CARD = { background: '#fff', borderRadius: '14px', padding: esMovil ? '14px' : '18px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', cursor: 'pointer', transition: 'box-shadow 0.15s, transform 0.15s' };
  const BTN = { border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: PP, transition: 'opacity 0.15s' };
  const INPUT = { width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e0e0e0', fontSize: '14px', fontFamily: PP, outline: 'none', boxSizing: 'border-box' };

  // ============================================
  // RENDER: FORMULARIO MULTI-PASO
  // ============================================
  const renderFormulario = () => {
    const puedeSiguiente1 = formData.cliente.trim() && formData.medio && (formData.clienteNuevo || formData.sucursal);
    const puedeSiguiente2 = formData.descripcion.trim() && formData.tipo;
    const indicadorPasos = (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
        {[1, 2, 3].map(p => (
          <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%',
              background: paso >= p ? colores.principal : '#e0e0e0',
              color: paso >= p ? '#fff' : colores.grisClaro,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: '700', fontFamily: PP,
            }}>{p}</div>
            {p < 3 && <div style={{ width: '24px', height: '2px', background: paso > p ? colores.principal : '#e0e0e0' }} />}
          </div>
        ))}
      </div>
    );

    return (
      <div onClick={() => resetFormulario()} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: esMovil ? 'flex-end' : 'center', justifyContent: 'center' }}>
        <div onClick={e => e.stopPropagation()} style={{
          background: '#fff', borderRadius: esMovil ? '20px 20px 0 0' : '20px',
          width: esMovil ? '100%' : '520px', maxHeight: esMovil ? '92vh' : '85vh',
          overflowY: 'auto', padding: esMovil ? '20px 16px 28px' : '28px',
          overscrollBehavior: 'contain',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: colores.gris, fontFamily: PP }}>
              {paso === 1 ? 'De quien viene?' : paso === 2 ? 'Que necesita?' : 'Confirmar'}
            </div>
            <div onClick={resetFormulario} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f3f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '16px', color: colores.grisClaro }}>
              x
            </div>
          </div>

          {indicadorPasos}

          {/* PASO 1 */}
          {paso === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Cliente */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '6px', display: 'block' }}>Cliente</label>
                {!formData.clienteNuevo ? (
                  <div>
                    <input
                      style={INPUT} placeholder="Buscar cliente..."
                      value={formData.cliente || buscandoCliente}
                      onChange={e => { setBuscandoCliente(e.target.value); setFormData(prev => ({ ...prev, cliente: '', sucursal: '' })); setBusquedaSucursal(''); setFiltroCiudad(''); }}
                    />
                    {clientesFiltrados.length > 0 && !formData.cliente && (
                      <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', marginTop: '4px', maxHeight: '150px', overflowY: 'auto' }}>
                        {clientesFiltrados.map(c => (
                          <div key={c} onClick={() => { setFormData(prev => ({ ...prev, cliente: c })); setBuscandoCliente(''); }}
                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '13px', fontFamily: PP, borderBottom: '1px solid #f3f4f4' }}>
                            {c}
                          </div>
                        ))}
                      </div>
                    )}
                    {formData.cliente && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', padding: '8px 12px', background: '#f0faf6', borderRadius: '8px' }}>
                        <span style={{ fontSize: '13px', fontFamily: PP, color: colores.gris, flex: 1 }}>{formData.cliente}</span>
                        <span onClick={() => { setFormData(prev => ({ ...prev, cliente: '', sucursal: '' })); setBusquedaSucursal(''); setFiltroCiudad(''); }} style={{ cursor: 'pointer', fontSize: '14px', color: colores.grisClaro }}>x</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <input style={INPUT} placeholder="Nombre del cliente nuevo" value={formData.cliente}
                    onChange={e => setFormData(prev => ({ ...prev, cliente: e.target.value }))} />
                )}
                <div
                  onClick={() => setFormData(prev => ({ ...prev, clienteNuevo: !prev.clienteNuevo, cliente: '', sucursal: '' }))}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', cursor: 'pointer', fontSize: '12px', color: colores.principal, fontFamily: PP, fontWeight: '500' }}
                >
                  <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${formData.clienteNuevo ? colores.principal : '#d0d0d0'}`, background: formData.clienteNuevo ? colores.principal : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff' }}>
                    {formData.clienteNuevo && '✓'}
                  </div>
                  Cliente nuevo
                </div>
              </div>

              {/* Sucursal — solo para clientes existentes */}
              {!formData.clienteNuevo && formData.cliente && (() => {
                const sucursalesRaw = SUCURSALES_POR_CLIENTE[formData.cliente] || SUCURSAL_DEFAULT;
                const todasOpciones = [...sucursalesRaw, SUCURSAL_NO_SE];

                // Ciudades únicas para chips
                const ciudades = [...new Set(sucursalesRaw.map(s => s.ciudad).filter(Boolean))];

                // Filtrar por ciudad
                let filtradas = filtroCiudad
                  ? todasOpciones.filter(s => s.ciudad === filtroCiudad || !s.ciudad)
                  : todasOpciones;

                // Búsqueda inteligente sin acentos
                if (busquedaSucursal.trim()) {
                  const q = normalizar(busquedaSucursal.trim());
                  const palabras = q.split(/\s+/);
                  filtradas = filtradas.map(s => {
                    const textoNombre = normalizar(s.nombre);
                    const textoDireccion = normalizar(s.direccion || '');
                    const textoCiudad = normalizar(s.ciudad || '');
                    const textoCompleto = textoNombre + ' ' + textoDireccion + ' ' + textoCiudad;
                    const coincide = palabras.every(p => textoCompleto.includes(p));
                    if (!coincide && !s.ciudad && s.nombre === SUCURSAL_NO_SE.nombre) return { ...s, _score: 99 };
                    if (!coincide) return null;
                    const enNombre = palabras.every(p => textoNombre.includes(p));
                    return { ...s, _score: enNombre ? 0 : 1 };
                  }).filter(Boolean);
                  filtradas.sort((a, b) => (a._score || 0) - (b._score || 0));
                }

                // Agrupar sucursales similares (mismo prefijo sin número final)
                const gruposSimilares = {};
                filtradas.forEach(s => {
                  const match = s.nombre.match(/^(.+?)\s*(\d+)$/);
                  if (match) {
                    const base = match[1].trim();
                    if (!gruposSimilares[base]) gruposSimilares[base] = [];
                    gruposSimilares[base].push(s);
                  }
                });

                return (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '6px', display: 'block' }}>En que sucursal?</label>

                    {/* Chips de ciudad */}
                    {ciudades.length > 1 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                        <div onClick={() => setFiltroCiudad('')}
                          style={{
                            padding: '5px 14px', borderRadius: '16px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', fontFamily: PP,
                            background: !filtroCiudad ? colores.principal : '#f3f4f4',
                            color: !filtroCiudad ? '#fff' : colores.grisClaro,
                            transition: 'all 0.15s',
                          }}>Todas</div>
                        {ciudades.map(c => {
                          const cc = COLORES_CIUDAD[c] || { bg: '#f3f4f4', color: colores.grisClaro };
                          const activa = filtroCiudad === c;
                          return (
                            <div key={c} onClick={() => setFiltroCiudad(activa ? '' : c)}
                              style={{
                                padding: '5px 14px', borderRadius: '16px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', fontFamily: PP,
                                background: activa ? cc.color : cc.bg,
                                color: activa ? '#fff' : cc.color,
                                transition: 'all 0.15s',
                              }}>{c}</div>
                          );
                        })}
                      </div>
                    )}

                    {/* Campo de búsqueda */}
                    {todasOpciones.length > 4 && (
                      <div style={{ position: 'relative', marginBottom: '10px' }}>
                        <input
                          style={{ width: '100%', padding: '10px 14px 10px 34px', borderRadius: '10px', border: '1px solid #e0e0e0', fontSize: '13px', fontFamily: PP, outline: 'none', boxSizing: 'border-box' }}
                          placeholder="Buscar sucursal, direccion, ciudad..."
                          value={busquedaSucursal}
                          onChange={e => setBusquedaSucursal(e.target.value)}
                        />
                        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: colores.grisClaro }}>{'🔍'}</span>
                        {busquedaSucursal && (
                          <span onClick={() => setBusquedaSucursal('')} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: colores.grisClaro, cursor: 'pointer' }}>x</span>
                        )}
                      </div>
                    )}

                    {/* Lista de sucursales */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '240px', overflowY: 'auto' }}>
                      {filtradas.length === 0 && (
                        <div style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: colores.grisClaro, fontFamily: PP }}>
                          No se encontraron sucursales
                        </div>
                      )}
                      {filtradas.map(s => {
                        const esSel = formData.sucursal && formData.sucursal.nombre === s.nombre;
                        const cc = s.ciudad ? (COLORES_CIUDAD[s.ciudad] || { bg: '#f3f4f4', color: colores.grisClaro }) : null;
                        // Resaltar número si hay grupo similar
                        const match = s.nombre.match(/^(.+?)\s*(\d+)$/);
                        const base = match ? match[1].trim() : null;
                        const num = match ? match[2] : null;
                        const esGrupo = base && gruposSimilares[base] && gruposSimilares[base].length > 1;

                        return (
                          <div key={s.nombre + s.direccion} onClick={() => { setFormData(prev => ({ ...prev, sucursal: s })); setBusquedaSucursal(''); }}
                            style={{
                              padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                              background: esSel ? '#eef5ff' : '#fafafa',
                              border: esSel ? `2px solid ${colores.principal}` : '2px solid transparent',
                              transition: 'all 0.15s',
                            }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: esSel ? '700' : '500', color: esSel ? colores.principal : colores.gris, fontFamily: PP }}>
                                  {esGrupo ? (
                                    <>{base} <span style={{ background: colores.principal + '20', color: colores.principal, padding: '1px 6px', borderRadius: '6px', fontWeight: '800', fontSize: '12px' }}>{num}</span></>
                                  ) : s.nombre}
                                </div>
                                {s.direccion && <div style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.direccion}</div>}
                              </div>
                              {cc && s.ciudad && (
                                <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', fontFamily: PP, background: cc.bg, color: cc.color, flexShrink: 0 }}>{s.ciudad}</span>
                              )}
                              {esSel && <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: colores.principal, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', flexShrink: 0 }}>{'✓'}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Medio */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '6px', display: 'block' }}>Medio de contacto</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {MEDIOS.map(m => (
                    <div key={m} onClick={() => setFormData(prev => ({ ...prev, medio: m }))}
                      style={{
                        padding: '8px 16px', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', fontFamily: PP,
                        background: formData.medio === m ? colores.principal : '#f3f4f4',
                        color: formData.medio === m ? '#fff' : colores.gris,
                        transition: 'all 0.15s',
                      }}>
                      {m}
                    </div>
                  ))}
                </div>
              </div>

              {/* Info automática */}
              <div style={{ padding: '12px 14px', background: '#f3f4f4', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Recibe: <strong style={{ color: colores.gris }}>{usuario}</strong></div>
                <div style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Fecha: <strong style={{ color: colores.gris }}>{new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></div>
              </div>

              <button disabled={!puedeSiguiente1} onClick={() => setPaso(2)}
                style={{ ...BTN, background: puedeSiguiente1 ? colores.principal : '#d0d0d0', color: '#fff', width: '100%', padding: '14px', fontSize: '15px' }}>
                Siguiente
              </button>
            </div>
          )}

          {/* PASO 2 */}
          {paso === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Descripción */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '6px', display: 'block' }}>Descripcion</label>
                <div style={{ position: 'relative' }}>
                  <textarea
                    style={{ ...INPUT, minHeight: '100px', resize: 'vertical', paddingRight: '50px' }}
                    placeholder="Describe lo que necesita el cliente..."
                    value={formData.descripcion}
                    onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  />
                  <div onClick={toggleVoz} style={{
                    position: 'absolute', right: '10px', top: '10px',
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: grabando ? colores.rojo : colores.principal,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', fontSize: '16px', color: '#fff',
                    boxShadow: grabando ? '0 0 0 4px rgba(224,92,92,0.3)' : 'none',
                    animation: grabando ? 'pulse 1.5s infinite' : 'none',
                  }}>
                    {grabando ? '⏹' : '🎤'}
                  </div>
                </div>
                {grabando && <div style={{ fontSize: '11px', color: colores.rojo, fontFamily: PP, marginTop: '4px' }}>Escuchando... habla ahora</div>}
              </div>

              {/* Tipo */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '6px', display: 'block' }}>Tipo de requerimiento</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {TIPOS.map(t => (
                    <div key={t.id} onClick={() => setFormData(prev => ({ ...prev, tipo: t.id }))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 14px', borderRadius: '12px', cursor: 'pointer',
                        background: formData.tipo === t.id ? t.bg : '#fafafa',
                        border: formData.tipo === t.id ? `2px solid ${t.color}` : '2px solid transparent',
                        transition: 'all 0.15s',
                      }}>
                      <span style={{ fontSize: '22px' }}>{t.icono}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, fontFamily: PP }}>{t.label}</div>
                        <div style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>{t.desc}</div>
                      </div>
                      {formData.tipo === t.id && <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff' }}>{'✓'}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Proyecto relacionado */}
              {formData.tipo === 'adicional' && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '6px', display: 'block' }}>Proyecto activo relacionado</label>
                  <select style={{ ...INPUT, appearance: 'auto' }} value={formData.proyectoRelacionado}
                    onChange={e => setFormData(prev => ({ ...prev, proyectoRelacionado: e.target.value }))}>
                    <option value="">Seleccionar proyecto...</option>
                    {PROYECTOS_ACTIVOS.map(p => <option key={p.id} value={p.nombre}>{p.nombre}</option>)}
                  </select>
                </div>
              )}

              {/* Urgencia */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '6px', display: 'block' }}>Urgencia</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {URGENCIAS.map(u => (
                    <div key={u.id} onClick={() => setFormData(prev => ({ ...prev, urgencia: u.id }))}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '10px', textAlign: 'center', cursor: 'pointer',
                        background: formData.urgencia === u.id ? u.bg : '#fafafa',
                        border: formData.urgencia === u.id ? `2px solid ${u.color}` : '2px solid transparent',
                        fontSize: '13px', fontWeight: '600', color: formData.urgencia === u.id ? u.color : colores.grisClaro,
                        fontFamily: PP, transition: 'all 0.15s',
                      }}>
                      {u.label}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setPaso(1)} style={{ ...BTN, background: '#f3f4f4', color: colores.gris, flex: 1, padding: '14px' }}>Atras</button>
                <button disabled={!puedeSiguiente2} onClick={() => setPaso(3)}
                  style={{ ...BTN, background: puedeSiguiente2 ? colores.principal : '#d0d0d0', color: '#fff', flex: 2, padding: '14px', fontSize: '15px' }}>
                  Siguiente
                </button>
              </div>
            </div>
          )}

          {/* PASO 3 — Resumen */}
          {paso === 3 && (() => {
            const tipoInfo = TIPOS.find(t => t.id === formData.tipo);
            const urgInfo = URGENCIAS.find(u => u.id === formData.urgencia);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ background: '#f3f4f4', borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Folio</span>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: colores.principal, fontFamily: PP }}>{generarFolioReq(requerimientos)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Cliente</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: colores.gris, fontFamily: PP }}>{formData.clienteNuevo ? `(Nuevo) ${formData.cliente}` : formData.cliente}</span>
                  </div>
                  {formData.sucursal && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Sucursal</span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '12px', color: colores.gris, fontFamily: PP, fontWeight: '600' }}>{formData.sucursal.nombre}</div>
                        {formData.sucursal.direccion && <div style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>{formData.sucursal.direccion}</div>}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Medio</span>
                    <span style={{ fontSize: '13px', color: colores.gris, fontFamily: PP }}>{formData.medio}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Tipo</span>
                    <span style={{ fontSize: '13px', fontFamily: PP, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{tipoInfo && tipoInfo.icono}</span>
                      <span style={{ color: tipoInfo ? tipoInfo.color : colores.gris, fontWeight: '600' }}>{tipoInfo && tipoInfo.label}</span>
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Urgencia</span>
                    <span style={{ padding: '3px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', fontFamily: PP, background: urgInfo ? urgInfo.bg : '#f3f4f4', color: urgInfo ? urgInfo.color : colores.gris }}>
                      {urgInfo && urgInfo.label}
                    </span>
                  </div>
                  {formData.proyectoRelacionado && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Proyecto</span>
                      <span style={{ fontSize: '12px', color: colores.gris, fontFamily: PP }}>{formData.proyectoRelacionado}</span>
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '10px' }}>
                    <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP, display: 'block', marginBottom: '4px' }}>Descripcion</span>
                    <span style={{ fontSize: '13px', color: colores.gris, fontFamily: PP, lineHeight: 1.5 }}>{formData.descripcion}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>Recibe</span>
                    <span style={{ fontSize: '12px', color: colores.gris, fontFamily: PP }}>{usuario}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setPaso(2)} style={{ ...BTN, background: '#f3f4f4', color: colores.gris, flex: 1, padding: '14px' }}>Atras</button>
                  <button onClick={crearRequerimiento}
                    style={{ ...BTN, background: colores.principal, color: '#fff', flex: 2, padding: '14px', fontSize: '15px' }}>
                    Crear requerimiento
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER: DETALLE DE REQUERIMIENTO
  // ============================================
  const renderDetalle = () => {
    if (!seleccionado) return null;
    const r = seleccionado;
    const tipoInfo = TIPOS.find(t => t.id === r.tipo);
    const urgInfo = URGENCIAS.find(u => u.id === r.urgencia);

    return (
      <div onClick={() => { setSeleccionado(null); setConfirmandoCierre(false); setMotivoCierre(''); }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: esMovil ? 'flex-end' : 'center', justifyContent: 'center' }}>
        <div onClick={e => e.stopPropagation()} style={{
          background: '#fff', borderRadius: esMovil ? '20px 20px 0 0' : '20px',
          width: esMovil ? '100%' : '600px', maxHeight: esMovil ? '92vh' : '85vh',
          overflowY: 'auto', padding: esMovil ? '20px 16px 28px' : '28px',
          overscrollBehavior: 'contain',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: colores.principal, fontFamily: PP, marginBottom: '4px' }}>{r.folio}</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: colores.gris, fontFamily: PP }}>{r.cliente}</div>
            </div>
            <div onClick={() => { setSeleccionado(null); setConfirmandoCierre(false); }} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f3f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '16px', color: colores.grisClaro }}>x</div>
          </div>

          {/* Info principal */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
            <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', fontFamily: PP, background: tipoInfo ? tipoInfo.bg : '#f3f4f4', color: tipoInfo ? tipoInfo.color : colores.gris }}>
              {tipoInfo && tipoInfo.icono} {tipoInfo && tipoInfo.label}
            </span>
            <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', fontFamily: PP, background: urgInfo ? urgInfo.bg : '#f3f4f4', color: urgInfo ? urgInfo.color : colores.gris }}>
              {urgInfo && urgInfo.label}
            </span>
            <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontFamily: PP, background: '#f3f4f4', color: colores.grisClaro }}>{r.medio}</span>
          </div>

          {/* Descripción */}
          <div style={{ fontSize: '14px', color: colores.gris, fontFamily: PP, lineHeight: 1.6, marginBottom: '16px', padding: '14px', background: '#f9fafb', borderRadius: '12px' }}>
            {r.descripcion}
          </div>

          {/* Resolución si existe */}
          {r.resolucion && (
            <div style={{ padding: '14px', background: '#f0faf6', borderRadius: '12px', marginBottom: '16px', borderLeft: `3px solid ${colores.verde}` }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: colores.verde, fontFamily: PP, marginBottom: '4px' }}>Resolucion</div>
              <div style={{ fontSize: '13px', color: colores.gris, fontFamily: PP, lineHeight: 1.5 }}>{r.resolucion}</div>
            </div>
          )}

          {/* Detalles */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px', fontSize: '12px', fontFamily: PP }}>
            <div style={{ padding: '10px', background: '#f3f4f4', borderRadius: '8px' }}>
              <div style={{ color: colores.grisClaro, marginBottom: '2px' }}>Recibio</div>
              <div style={{ color: colores.gris, fontWeight: '600' }}>{r.recibio}</div>
            </div>
            <div style={{ padding: '10px', background: '#f3f4f4', borderRadius: '8px' }}>
              <div style={{ color: colores.grisClaro, marginBottom: '2px' }}>Fecha</div>
              <div style={{ color: colores.gris, fontWeight: '600' }}>{formatFecha(r.fecha)}</div>
            </div>
            {r.asignado && (
              <div style={{ padding: '10px', background: '#eef5ff', borderRadius: '8px' }}>
                <div style={{ color: colores.grisClaro, marginBottom: '2px' }}>Asignado a</div>
                <div style={{ color: colores.principal, fontWeight: '600' }}>{r.asignado}</div>
              </div>
            )}
            {r.proyectoRelacionado && (
              <div style={{ padding: '10px', background: '#f5f0ff', borderRadius: '8px' }}>
                <div style={{ color: colores.grisClaro, marginBottom: '2px' }}>Proyecto</div>
                <div style={{ color: '#a78bfa', fontWeight: '600', fontSize: '11px' }}>{r.proyectoRelacionado}</div>
              </div>
            )}
          </div>

          {/* Historial del cliente */}
          {r.historialCliente && r.historialCliente.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: colores.gris, fontFamily: PP, marginBottom: '10px' }}>Historial del cliente</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {r.historialCliente.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#f9fafb', borderRadius: '8px' }}>
                    <span style={{ fontSize: '14px' }}>{h.tipo === 'servicio' ? '🔧' : h.tipo === 'proyecto' ? '📐' : h.tipo === 'equipo' ? '📦' : '📄'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: colores.gris, fontFamily: PP }}>{h.descripcion}</div>
                      <div style={{ fontSize: '10px', color: colores.grisClaro, fontFamily: PP }}>{h.fecha}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: colores.gris, fontFamily: PP, marginBottom: '10px' }}>Linea de tiempo</div>
            <div style={{ position: 'relative', paddingLeft: '20px' }}>
              <div style={{ position: 'absolute', left: '6px', top: '4px', bottom: '4px', width: '2px', background: '#e0e0e0' }} />
              {r.timeline.map((t, i) => (
                <div key={i} style={{ position: 'relative', marginBottom: '14px' }}>
                  <div style={{ position: 'absolute', left: '-17px', top: '4px', width: '10px', height: '10px', borderRadius: '50%', background: i === r.timeline.length - 1 ? colores.principal : '#d0d0d0', border: '2px solid #fff' }} />
                  <div style={{ fontSize: '12px', color: colores.gris, fontFamily: PP, fontWeight: '500' }}>{t.texto}</div>
                  <div style={{ fontSize: '10px', color: colores.grisClaro, fontFamily: PP }}>{formatFecha(t.fecha)} — {t.autor}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Botones de acción */}
          {r.estado !== 'historial' && (
            <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
              {[
                { label: 'Resolver remotamente', icono: '✅', color: colores.verde, bg: '#f0faf6' },
                { label: 'Programar visita', icono: '📅', color: '#f4a261', bg: '#fff8f0' },
                { label: 'Crear proyecto', icono: '📐', color: colores.principal, bg: '#eef5ff' },
                { label: 'Enviar cotizacion', icono: '📝', color: '#a78bfa', bg: '#f5f0ff' },
              ].map((a, i) => (
                <div key={i} style={{ padding: '12px 8px', borderRadius: '12px', background: a.bg, textAlign: 'center', cursor: 'pointer', transition: 'transform 0.15s' }}>
                  <div style={{ fontSize: '22px', marginBottom: '4px' }}>{a.icono}</div>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: a.color, fontFamily: PP }}>{a.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Notas */}
          {r.notas && r.notas.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: colores.gris, fontFamily: PP, marginBottom: '8px' }}>Notas</div>
              {r.notas.map((n, i) => (
                <div key={i} style={{ padding: '10px 12px', background: '#fffbe6', borderRadius: '8px', marginBottom: '6px' }}>
                  <div style={{ fontSize: '12px', color: colores.gris, fontFamily: PP }}>{n.texto}</div>
                  <div style={{ fontSize: '10px', color: colores.grisClaro, fontFamily: PP, marginTop: '4px' }}>{n.autor} — {formatFecha(n.fecha)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Agregar nota */}
          {r.estado !== 'historial' && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input style={{ ...INPUT, flex: 1 }} placeholder="Agregar nota..." value={nuevaNota} onChange={e => setNuevaNota(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && agregarNota()} />
              <button onClick={agregarNota} style={{ ...BTN, background: colores.principal, color: '#fff', padding: '10px 16px' }}>Enviar</button>
            </div>
          )}

          {/* Cerrar requerimiento */}
          {r.estado !== 'historial' && !confirmandoCierre && (
            <button onClick={() => setConfirmandoCierre(true)}
              style={{ ...BTN, background: '#fff0f0', color: colores.rojo, width: '100%', padding: '12px', border: `1px solid ${colores.rojo}30` }}>
              Cerrar requerimiento
            </button>
          )}
          {confirmandoCierre && (
            <div style={{ padding: '16px', background: '#fff0f0', borderRadius: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: colores.rojo, fontFamily: PP, marginBottom: '8px' }}>Motivo de cierre</div>
              <textarea style={{ ...INPUT, minHeight: '60px', marginBottom: '10px' }} placeholder="Describe como se resolvio..."
                value={motivoCierre} onChange={e => setMotivoCierre(e.target.value)} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setConfirmandoCierre(false)} style={{ ...BTN, background: '#f3f4f4', color: colores.gris, flex: 1 }}>Cancelar</button>
                <button onClick={cerrarRequerimiento} disabled={!motivoCierre.trim()}
                  style={{ ...BTN, background: motivoCierre.trim() ? colores.rojo : '#d0d0d0', color: '#fff', flex: 1 }}>
                  Confirmar cierre
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER: TARJETA DE REQUERIMIENTO
  // ============================================
  const renderTarjeta = (r) => {
    const tipoInfo = TIPOS.find(t => t.id === r.tipo);
    const urgInfo = URGENCIAS.find(u => u.id === r.urgencia);
    return (
      <div key={r.id} onClick={() => setSeleccionado(r)} style={{ ...CARD, borderLeft: `3px solid ${tipoInfo ? tipoInfo.color : '#d0d0d0'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: colores.principal, fontFamily: PP }}>{r.folio}</span>
            <span style={{ padding: '2px 10px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', fontFamily: PP, background: urgInfo ? urgInfo.bg : '#f3f4f4', color: urgInfo ? urgInfo.color : colores.gris }}>
              {urgInfo && urgInfo.label}
            </span>
          </div>
          <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>{tiempoTranscurrido(r.fecha)}</span>
        </div>

        <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '4px' }}>{r.cliente}</div>

        <div style={{ fontSize: '12px', color: colores.grisClaro, fontFamily: PP, marginBottom: '10px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5 }}>
          {r.descripcion}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>{tipoInfo && tipoInfo.icono}</span>
            <span style={{ fontSize: '11px', color: tipoInfo ? tipoInfo.color : colores.grisClaro, fontWeight: '600', fontFamily: PP }}>{tipoInfo && tipoInfo.label}</span>
          </div>
          <span style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP }}>
            {r.recibio}{r.asignado ? ` → ${r.asignado}` : ''}
          </span>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER PRINCIPAL
  // ============================================
  return (
    <div style={{ fontFamily: PP, maxWidth: '900px', margin: '0 auto' }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(224,92,92,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(224,92,92,0); }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: esMovil ? '20px' : '24px', fontWeight: '800', color: colores.gris, fontFamily: PP }}>Requerimientos</div>
          <div style={{ fontSize: '12px', color: colores.grisClaro, fontFamily: PP }}>{requerimientos.length} totales</div>
        </div>
      </div>

      {/* Pestañas */}
      <BarraPestanas
        tabs={[
          { key: 'nuevo', label: `Nuevos (${conteos.nuevo})` },
          { key: 'enproceso', label: `En proceso (${conteos.enproceso})` },
          { key: 'historial', label: `Historial (${conteos.historial})` },
        ]}
        activo={pestana}
        onChange={setPestana}
      />

      {/* Búsqueda */}
      <div style={{ margin: '14px 0' }}>
        <BarraBusqueda value={busqueda} onChange={setBusqueda} placeholder="Buscar por folio, cliente, descripcion..." />
      </div>

      {/* Lista */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '80px' }}>
        {filtrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>{'📋'}</div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '4px' }}>Sin requerimientos</div>
            <div style={{ fontSize: '13px', color: colores.grisClaro, fontFamily: PP }}>
              {pestana === 'nuevo' ? 'No hay requerimientos nuevos por atender' :
                pestana === 'enproceso' ? 'No hay requerimientos en proceso' : 'No hay requerimientos en historial'}
            </div>
          </div>
        ) : (
          (() => {
            // Agrupar por cliente + sucursal
            const grupos = {};
            const orden = [];
            filtrados.forEach(r => {
              const key = `${r.cliente}||${r.sucursal || ''}`;
              if (!grupos[key]) {
                grupos[key] = { cliente: r.cliente, sucursal: r.sucursal || '', items: [] };
                orden.push(key);
              }
              grupos[key].items.push(r);
            });
            return orden.map((key, idx) => {
              const g = grupos[key];
              const pendientesCount = g.items.filter(r => r.estado !== 'historial').length;
              return (
                <div key={key} style={{
                  display: 'flex', flexDirection: 'column', gap: '8px',
                  paddingBottom: idx < orden.length - 1 ? '12px' : 0,
                  borderBottom: idx < orden.length - 1 ? `1px solid ${colores.principal}22` : 'none',
                  marginBottom: idx < orden.length - 1 ? '6px' : 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: colores.oscuro, fontFamily: PP, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {g.cliente}
                      </div>
                      {g.sucursal && (
                        <div style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {g.sucursal}
                        </div>
                      )}
                    </div>
                    {pendientesCount > 0 && (
                      <span style={{
                        background: colores.oscuro, color: '#fff',
                        borderRadius: '12px', padding: '2px 10px',
                        fontSize: '11px', fontWeight: '700', fontFamily: PP,
                      }}>
                        {pendientesCount} pendiente{pendientesCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {g.items.map(r => renderTarjeta(r))}
                </div>
              );
            });
          })()
        )}
      </div>

      {/* Botón flotante + */}
      <div onClick={() => setFormularioAbierto(true)} style={{
        position: 'fixed', bottom: esMovil ? '85px' : '28px', right: '20px',
        width: '56px', height: '56px', borderRadius: '50%',
        background: colores.principal, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '28px', fontWeight: '300',
        boxShadow: '0 4px 16px rgba(92,154,219,0.4)',
        cursor: 'pointer', zIndex: 50,
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}>
        +
      </div>

      {/* Modales */}
      {formularioAbierto && renderFormulario()}
      {seleccionado && renderDetalle()}
      <ModoLevantamiento
        abierto={modoLevantamiento}
        onCerrar={() => setModoLevantamiento(false)}
        onCrear={crearDesdeIA}
        esMovil={esMovil}
      />

      {/* Modal agrupar requerimientos del mismo cliente+sucursal */}
      {modalAgrupar && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '20px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '24px',
            maxWidth: '420px', width: '100%', fontFamily: PP,
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: colores.oscuro, marginBottom: '10px' }}>
              ¿Agrupar requerimientos?
            </div>
            <div style={{ fontSize: '13px', color: colores.gris, lineHeight: 1.5, marginBottom: '20px' }}>
              Ya tienes {modalAgrupar.existentes.length} requerimiento{modalAgrupar.existentes.length > 1 ? 's' : ''} pendiente{modalAgrupar.existentes.length > 1 ? 's' : ''} de{' '}
              <strong>{modalAgrupar.nuevo.cliente}</strong>
              {modalAgrupar.nuevo.sucursal && <> — <strong>{modalAgrupar.nuevo.sucursal}</strong></>}.
              {' '}¿Deseas agruparlos para atenderlos en una sola visita?
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => confirmarAgrupar(false)}
                style={{
                  padding: '10px 16px', borderRadius: '10px',
                  border: `1px solid ${colores.principal}`, background: '#fff',
                  color: colores.principal, fontWeight: '600', cursor: 'pointer',
                  fontSize: '13px', fontFamily: PP,
                }}
              >
                No, crear por separado
              </button>
              <button
                onClick={() => confirmarAgrupar(true)}
                style={{
                  padding: '10px 16px', borderRadius: '10px',
                  border: 'none', background: colores.oscuro,
                  color: '#fff', fontWeight: '600', cursor: 'pointer',
                  fontSize: '13px', fontFamily: PP,
                }}
              >
                Sí, agrupar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Requerimientos;
